import { useEffect, useState } from 'react';
import { useCoralTheme } from '@krakentech/coral';
import { motion } from 'framer-motion';
import styled from 'styled-components';

// Configuration for a single confetti particle
type ParticleConfig = {
  id: number;
  delay: number;
  color: string;
  shape: 'circle' | 'square' | 'triangle';
  size: number;
  initialX: number;
  rotation: number;
  fallDuration: number;
  horizontalDrift: number;
};

type ConfettiSystemProps = {
  isActive?: boolean; // Whether the confetti animation is playing
  intensity?: number; // Number of particles to generate
};

// Container that covers the full viewport
const Container = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none; // Allows clicking through the confetti
  z-index: 50;
  overflow: hidden;
`;

// Base styling for all particle shapes
const ParticleBase = styled(motion.div)<{ $shape: string }>`
  position: absolute;
  ${({ $shape }) => $shape === 'circle' && 'border-radius: 50%;'}
  ${({ $shape }) => $shape === 'square' && 'border-radius: 2px;'}
  ${({ $shape }) =>
    $shape === 'triangle' &&
    `
    border-radius: 2px;
    transform: rotate(45deg);
  `}
`;

// Individual confetti particle with animation
const ConfettiParticle = ({
  delay,
  color,
  shape,
  size,
  initialX,
  rotation,
  fallDuration,
  horizontalDrift,
}: ParticleConfig) => {
  return (
    <ParticleBase
      aria-hidden="true"
      $shape={shape}
      style={{
        backgroundColor: color,
        width: size,
        height: size,
        left: initialX,
        top: -20,
      }}
      initial={{
        y: -20,
        opacity: 0,
        rotate: 0,
        scale: 1,
      }}
      animate={{
        y: window.innerHeight + 50, // Fall past bottom of screen
        opacity: [0, 1, 1, 0], // Fade in and out
        rotate: rotation + 720, // Spin twice plus random rotation
        x: initialX + horizontalDrift, // Drift horizontally while falling
        scale: [1, 0.8, 1, 0.6], // Subtle size pulsing
      }}
      transition={{
        duration: fallDuration,
        delay: delay,
        ease: 'easeIn',
        opacity: {
          duration: fallDuration,
          times: [0, 0.1, 0.9, 1],
        },
        scale: {
          duration: fallDuration,
          times: [0, 0.3, 0.7, 1],
        },
      }}
    />
  );
};

// Main confetti system that manages particle generation
const ConfettiSystem = ({
  isActive = true,
  intensity = 50,
}: ConfettiSystemProps) => {
  const { color } = useCoralTheme();
  // Use brand colors from theme
  const confettiColors = [
    color.primary.main,
    color.secondary.dark,
    color.tertiary.light,
  ];
  const shapes = ['circle', 'square', 'triangle'] as const;

  const [particles, setParticles] = useState<ParticleConfig[]>([]);

  // Creates a new set of particles with randomized properties
  const generateParticles = () => {
    return Array.from({ length: intensity }).map((_, i) => ({
      id: i,
      delay: i * 0.05, // Stagger particle start times
      color: confettiColors[i % confettiColors.length],
      shape: shapes[i % shapes.length],
      size: 8 + Math.random() * 8, // Random size between 8-16
      initialX: Math.random() * window.innerWidth,
      rotation: Math.random() * 360,
      fallDuration: 3 + Math.random() * 2, // Fall for 3-5 seconds
      horizontalDrift: (Math.random() - 0.5) * 300, // Drift left or right
    }));
  };

  // Regenerate particles when animation is activated
  useEffect(() => {
    setParticles(isActive ? generateParticles() : []);
  }, [isActive, intensity, confettiColors.join(','), shapes.join(',')]);

  if (!isActive || particles.length === 0) return null;

  return (
    <Container>
      {particles.map((particle) => (
        <ConfettiParticle key={particle.id} {...particle} />
      ))}
    </Container>
  );
};

export default ConfettiSystem;
